import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Panel, PageHead } from "@/components/Panel";
import CTABand from "@/components/CTABand";

const phases = [
  {
    num: "01",
    title: "Discovery & Assessment",
    text: "We evaluate your current operations end to end: systems, workflows, data, team structure, and handoffs. The output is a clear picture of where revenue is leaking and why — grounded in your data, not in assumptions.",
  },
  {
    num: "02",
    title: "Strategy Development",
    text: "We build a roadmap tailored to your business: what changes, in what order, owned by whom, and what each phase is expected to produce. Sequenced for alignment first and scalability second.",
  },
  {
    num: "03",
    title: "Implementation",
    text: "We deploy the tools, processes, and training. This is hands-on work — configuring systems, rewriting workflows, and enabling your team so the change actually holds after we leave.",
  },
  {
    num: "04",
    title: "Continuous Optimization",
    text: "Revenue operations are never finished. We provide ongoing support to refine what's running, adapt as you grow, and scale the system alongside the business.",
  },
];

export default function Approach() {
  return (
    <div data-testid="approach-page">
      <SEO
        title="Our RevOps Approach — Assess, Strategize, Implement, Optimize | RevGen Genius"
        description="A four-phase RevOps methodology: discovery and assessment, strategy development, implementation, and continuous optimization."
        path="/approach"
      />
      <div className="mx-auto max-w-[1240px] space-y-24 px-5 sm:px-8">
        <PageHead
          testId="approach-head"
          label="Our Approach"
          title="How we work"
          intro="Four phases. No surprises. You'll know what's happening, what it costs, and what it's supposed to produce before we start."
        />

        <div className="space-y-6">
          {phases.map((p, i) => (
            <Reveal key={p.num} delay={0.05}>
              <Panel className="!py-12">
                <div className="grid items-start gap-6 md:grid-cols-[140px_1fr] lg:grid-cols-[180px_1fr]">
                  <p
                    data-testid={`phase-number-${i}`}
                    className="font-heading text-6xl font-bold leading-none text-[#1F5FFF] lg:text-7xl"
                    aria-hidden="true"
                  >
                    {p.num}
                  </p>
                  <div>
                    <h2 className="font-heading text-2xl font-semibold sm:text-3xl">{p.title}</h2>
                    <p className="mt-4 max-w-2xl leading-relaxed text-[#5B6784]">{p.text}</p>
                  </div>
                </div>
              </Panel>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <Panel label="What You Get" tint className="pt-16">
            <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
              <div>
                <h2 className="h2">Built to run without us.</h2>
                <p className="lead mt-5">
                  Every engagement produces documented processes, working systems, dashboards your
                  team owns, and a trained team that can run the engine without us.
                </p>
              </div>
              <div className="relative overflow-hidden rounded-[28px]">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=70"
                  alt="Revenue team in a working session around a table with laptops"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
                <div className="absolute bottom-4 left-4 rounded-2xl bg-[#1F5FFF] px-5 py-3 font-heading text-sm font-semibold text-white shadow-[0_2px_12px_rgba(31,95,255,0.25)]">
                  In the room with your revenue team.
                </div>
              </div>
            </div>
          </Panel>
        </Reveal>

        <CTABand
          title="Phase one is free."
          text="The discovery and assessment conversation costs you nothing but thirty minutes. Book it and find out what's actually slowing your revenue engine down."
          testId="approach-cta-book-button"
        />
      </div>
    </div>
  );
}
